import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import type { VideoOperationResult } from '../../../domain';
import { formatPrice, type NairaRate } from '../../../credits/naira';
import { elapsedSince, RENDER_STALLED_TEXT, renderExpectation, renderHeadline } from './renderProgress';

export type ClipCanvasPhase = 'empty' | 'rendering' | 'ready' | 'failed';

export interface ClipCanvasProps {
  /** Terminal Completed job → drives the player. */
  clip?: VideoOperationResult;
  /** In-flight snapshot → drives the progress surface. */
  rendering?: VideoOperationResult;
  /** Phase override (else derived from clip/rendering/error). */
  phase?: ClipCanvasPhase;
  errorMessage?: string;
  retryable?: boolean;
  /** Preview still shown as the player poster before/while rendering (Animate bridge). */
  posterUrl?: string;
  onCancel?: () => void;
  onRetry?: () => void;
  /** Polling has paused after a long render; the job keeps going on the server. */
  stalled?: boolean;
  /** Ask the server again now (and resume polling). */
  onCheckNow?: () => void;
  /** The wallet's Naira per dollar for the finished clip's model, shown beside its cost. */
  nairaRate?: NairaRate | null;
}

function derivePhase(props: ClipCanvasProps): ClipCanvasPhase {
  if (props.phase) return props.phase;
  if (props.errorMessage) return 'failed';
  if (props.clip?.status === 'Completed' && props.clip.result) return 'ready';
  if (props.rendering) return 'rendering';
  return 'empty';
}

/**
 * Animation Lab center canvas (A-F2) — a single-clip `<video>` player with four
 * states. Media loads via `signedUrl` (no x-user-id header on element loads, the
 * same constraint that bit stills); never throws on a null poster (A-B7).
 */
export const ClipCanvas: React.FC<ClipCanvasProps> = props => {
  const phase = derivePhase(props);

  return (
    <Box
      data-testid="clip-canvas"
      data-phase={phase}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      {phase === 'empty' && <EmptyState />}
      {phase === 'rendering' && (
        <RenderingState
          job={props.rendering}
          posterUrl={props.posterUrl}
          onCancel={props.onCancel}
          stalled={props.stalled}
          onCheckNow={props.onCheckNow}
        />
      )}
      {phase === 'ready' && props.clip?.result && <ReadyState clip={props.clip} nairaRate={props.nairaRate} />}
      {phase === 'failed' && (
        <FailedState
          message={props.errorMessage ?? 'Render failed.'}
          retryable={props.retryable}
          onRetry={props.onRetry}
        />
      )}
    </Box>
  );
};

const EmptyState: React.FC = () => (
  <Stack alignItems="center" spacing={1.5} sx={{ color: 'text.secondary', textAlign: 'center' }}>
    <MovieCreationOutlinedIcon sx={{ fontSize: 56, opacity: 0.5 }} />
    <Typography variant="body1">No clip yet.</Typography>
    <Typography variant="body2">Generate one from a prompt or animate a still.</Typography>
  </Stack>
);

const RenderingState: React.FC<{
  job?: VideoOperationResult;
  posterUrl?: string;
  onCancel?: () => void;
  stalled?: boolean;
  onCheckNow?: () => void;
}> = ({ job, posterUrl, onCancel, stalled, onCheckNow }) => {
  // Elapsed time ticks locally between polls, from the server's start time when it sent one.
  const seenAt = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = elapsedSince(job?.startedAt, seenAt.current, now);
  return (
    <Stack alignItems="center" spacing={2} sx={{ width: '100%', maxWidth: 420 }}>
      {posterUrl && (
        <Box
          component="img"
          src={posterUrl}
          alt="Source still"
          sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1, opacity: 0.6 }}
        />
      )}
      <CircularProgress />
      <Typography variant="body2" color="text.primary" data-testid="render-headline">
        {renderHeadline(job, elapsed)}
      </Typography>
      <Typography variant="caption" color="text.secondary" textAlign="center" data-testid="render-expectation">
        {stalled ? RENDER_STALLED_TEXT : renderExpectation(job, elapsed)}
      </Typography>
      {/* fal reports no percentage, so the bar shows activity, not an invented amount done. */}
      <Box sx={{ width: '100%' }}>
        <LinearProgress variant="indeterminate" aria-label="Rendering" />
      </Box>
      {stalled && onCheckNow && (
        <Button size="small" variant="contained" onClick={onCheckNow}>
          Check now
        </Button>
      )}
      {onCancel && (
        <Button size="small" color="inherit" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </Stack>
  );
};

const ReadyState: React.FC<{ clip: VideoOperationResult; nairaRate?: NairaRate | null }> = ({ clip, nairaRate }) => {
  const result = clip.result!;
  const src = result.signedUrl ?? result.url;
  const poster = result.posterSignedUrl ?? result.posterUrl ?? undefined;
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ width: '100%', height: '100%' }}>
      <Box
        component="video"
        data-testid="clip-video"
        src={src}
        poster={poster}
        controls
        sx={{ maxWidth: '100%', maxHeight: '100%', minHeight: 0, borderRadius: 1, bgcolor: 'black' }}
      />
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {result.durationSeconds}s · {result.resolution} · {result.aspectRatio}
          {result.modelUsed ? ` · ${result.modelUsed}` : ''}
          {clip.usage?.estimatedCost != null && formatPrice(clip.usage.estimatedCost, nairaRate)
            ? ` · ${formatPrice(clip.usage.estimatedCost, nairaRate)}`
            : ''}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          href={src}
          download
          component="a"
        >
          Download
        </Button>
      </Stack>
    </Stack>
  );
};

const FailedState: React.FC<{
  message: string;
  retryable?: boolean;
  onRetry?: () => void;
}> = ({ message, retryable, onRetry }) => (
  <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', maxWidth: 420 }}>
    <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
    <Typography variant="body2" color="error">
      {message}
    </Typography>
    {onRetry && (
      <Button size="small" variant="outlined" onClick={onRetry} disabled={retryable === false}>
        Retry
      </Button>
    )}
  </Stack>
);
